import {
  keepPreviousData,
  queryOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { z } from 'zod';
import { ENDPOINT, PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import { type TimestampUTC, type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils';

import { queryKeysInvoiceAiModel } from './queryKeys';
import { type AiModelSettings } from './useQueryInvoiceAiModelSettings';

const zodInvoiceAiModelsSettingsSearchParametersSchema = z
  .object({
    limit: z.coerce
      .number()
      .nonnegative()
      .max(500)
      .optional()
      .prefault(PAGINATION_PAGE_SIZE_DEFAULT)
      .catch(PAGINATION_PAGE_SIZE_DEFAULT),
    offset: z.coerce.number().nonnegative().optional().prefault(0).catch(0),
    searchString: z.string().optional(),
  })
  .transform((data) =>
    withoutObjectKeysWhereValueIs(data, [null, undefined, '']),
  );

export type InvoiceAiModelsSettingsSearchParameters = z.infer<
  typeof zodInvoiceAiModelsSettingsSearchParametersSchema
>;

export type InvoiceAiModelsSettingsListItem = {
  aiModelSettings: AiModelSettings;
  createdOn: TimestampUTC;
  id: UUID;
  modifiedOn: TimestampUTC;
};

export type InvoiceAiModelsSettingsResponse = {
  data: InvoiceAiModelsSettingsListItem[];
  hasNextPage: boolean;
  paginatedCount: number;
  totalCount: number;
};

export type GetInvoiceAiModelsSettingsPayload = z.infer<
  typeof zodInvoiceAiModelsSettingsSearchParametersSchema
>;

/**
 * Fetches all invoice AI model settings from the API.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/get_invoice_ai_model_settings_vadmin_invoice_ai_model_settings_get
 */
export const fetchInvoiceAiModelsSettings = async (
  searchParams: GetInvoiceAiModelsSettingsPayload,
) => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.INVOICE_AI_MODEL.SETTINGS.GET_ALL(), {
        searchParams,
        validate: {
          searchParams: zodInvoiceAiModelsSettingsSearchParametersSchema,
        },
      })
      .json<InvoiceAiModelsSettingsResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error fetching invoice AI model settings',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error; // re-throw error so it can be handled higher up in the callstack.
  }
};

/**
 * Generates the query options for the invoice AI model settings query so they can be shared between the useQuery hook and the prefetching.
 */
export const getInvoiceAiModelsSettingsQueryOptions = ({
  options = {},
  prefetch = false,
  queryParameters,
}: {
  options?: Omit<
    UseQueryOptions<InvoiceAiModelsSettingsResponse>,
    'queryKey' | 'queryFn'
  >;
  prefetch?: boolean;
  queryParameters: Partial<GetInvoiceAiModelsSettingsPayload>;
}): UseQueryOptions<InvoiceAiModelsSettingsResponse> => {
  const validatedSearchParams =
    zodInvoiceAiModelsSettingsSearchParametersSchema.parse(queryParameters);

  if (prefetch) {
    validatedSearchParams.offset =
      (validatedSearchParams.offset ?? 0) +
      (validatedSearchParams.limit ?? PAGINATION_PAGE_SIZE_DEFAULT);
  }

  return queryOptions({
    async queryFn({ queryKey }) {
      const searchParams = {
        ...(queryKey[3] as InvoiceAiModelsSettingsSearchParameters), // TODO: query keys should be structured as one object as first array element; getting queryKey[3] is brittle
      };

      return fetchInvoiceAiModelsSettings(searchParams);
    },
    queryKey: queryKeysInvoiceAiModel.getAllSettings(validatedSearchParams),
    staleTime: ms('30s'),
    ...options,
  });
};

/**
 * React Query based custom hook for getting the data for all invoice AI model settings with given query parameters.
 * Handles pagination and prefetches the next page of invoice AI model settings for better performance.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/get_invoice_ai_model_settings_vadmin_invoice_ai_model_settings_get
 */
export const useQueryInvoiceAiModelsSettings = (
  queryParameters: Partial<GetInvoiceAiModelsSettingsPayload>,
  options?: Omit<
    UseQueryOptions<InvoiceAiModelsSettingsResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryClient = useQueryClient();

  void queryClient.prefetchQuery(
    getInvoiceAiModelsSettingsQueryOptions({
      options,
      prefetch: true,
      queryParameters,
    }),
  );

  return useQuery<InvoiceAiModelsSettingsResponse>({
    ...getInvoiceAiModelsSettingsQueryOptions({
      options,
      queryParameters,
    }),
    placeholderData: keepPreviousData,
  });
};
